import { useState } from 'react';
import { Bookmark, Plus, X } from 'lucide-react';
import Modal from './ui/Modal';
import { useConfirm } from './ui/ConfirmDialog';

interface View {
  name: string;
  /** URL query string of the Domains page filters, without page/sort. */
  query: string;
}

// Built-in views. Each is just a set of the page's existing URL filters.
const PRESETS: View[] = [
  { name: 'All domains', query: '' },
  { name: 'Expiring ≤ 30 days', query: 'expiring_days=30' },
  { name: 'Auto-renew off', query: 'auto_renew=false' },
  { name: 'Exposed + live', query: 'exposed=true&liveness_status=live' },
  { name: 'Live', query: 'liveness_status=live' },
  { name: 'Parked', query: 'liveness_status=parked' },
  { name: 'Unclassified', query: 'domain_type=__none__' },
  { name: 'Missed renewals', query: 'renewal_missed=true' },
  { name: 'Moved away', query: 'moved_away=true' },
  { name: 'Geo ≠ Cove', query: 'geo_mismatch=true' },
  { name: 'Transferred out', query: 'status=transferred_out' },
];

const KEY = 'domain-views';
// Params that are view state rather than filters.
const NON_FILTER = new Set(['page', 'sort_by', 'sort_order']);

/** Canonical filter string for comparing the current URL with a view. */
export function filterQuery(params: URLSearchParams): string {
  const entries = [...params.entries()].filter(([k, v]) => !NON_FILTER.has(k) && v !== '').sort(([a], [b]) => a.localeCompare(b));
  return new URLSearchParams(entries).toString();
}

function loadSaved(): View[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => x && typeof x.name === 'string' && typeof x.query === 'string') : [];
  } catch {
    return [];
  }
}

function storeSaved(views: View[]) {
  try { localStorage.setItem(KEY, JSON.stringify(views)); } catch { /* storage blocked */ }
}

/**
 * Row of quick views above the Domains table. Picking one replaces the
 * filters; "Save view" stores the current filters under a name (per browser).
 */
export default function DomainViews({ params, onApply }: { params: URLSearchParams; onApply: (query: string) => void }) {
  const confirm = useConfirm();
  const [saved, setSaved] = useState<View[]>(loadSaved);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const current = filterQuery(params);
  const canonical = (q: string) => filterQuery(new URLSearchParams(q));
  const matchesAny = [...PRESETS, ...saved].some((v) => canonical(v.query) === current);

  const save = () => {
    const n = name.trim();
    if (!n) return;
    const next = [...saved.filter((v) => v.name !== n), { name: n, query: current }];
    setSaved(next);
    storeSaved(next);
    setNaming(false);
    setName('');
  };

  const remove = async (v: View) => {
    if (!(await confirm({ title: `Delete view “${v.name}”?`, message: 'Only the saved shortcut is removed; no domains change.', tone: 'danger' }))) return;
    const next = saved.filter((x) => x.name !== v.name);
    setSaved(next);
    storeSaved(next);
  };

  const pill = (v: View, removable: boolean) => {
    const on = canonical(v.query) === current;
    return (
      <span key={(removable ? 's:' : 'p:') + v.name} className="group relative inline-flex">
        <button
          onClick={() => onApply(v.query)}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            on
              ? 'border-brand-300 bg-brand-50 text-brand-700'
              : 'border-slate-200 bg-surface text-slate-600 hover:border-slate-300 hover:text-slate-900'
          } ${removable ? 'pr-6' : ''}`}
        >
          {removable && <Bookmark className="h-3 w-3" />}
          {v.name}
        </button>
        {removable && (
          <button
            onClick={() => remove(v)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 opacity-0 transition-opacity hover:text-slate-700 group-hover:opacity-100 focus:opacity-100"
            title={`Delete view “${v.name}”`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  };

  return (
    <div className="-mx-1 flex items-center gap-1.5 overflow-x-auto px-1 pb-1">
      {PRESETS.map((v) => pill(v, false))}
      {saved.length > 0 && <span className="mx-1 h-4 w-px shrink-0 bg-slate-200" />}
      {saved.map((v) => pill(v, true))}
      {current && !matchesAny && (
        <button
          onClick={() => setNaming(true)}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs font-medium text-slate-500 hover:border-brand-300 hover:text-brand-700"
        >
          <Plus className="h-3 w-3" /> Save view
        </button>
      )}
      {naming && (
        <Modal
          size="sm"
          title="Save view"
          description="Saves the current filters as a shortcut in this browser."
          onClose={() => setNaming(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setNaming(false)}>Cancel</button>
              <button className="btn btn-primary" form="save-view-form" type="submit" disabled={!name.trim()}>Save</button>
            </>
          }
        >
          <form id="save-view-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
            <label className="label" htmlFor="view-name">Name</label>
            <input id="view-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AU casino, expiring" maxLength={60} />
          </form>
        </Modal>
      )}
    </div>
  );
}
